import { useCallback, useEffect, useRef, useState } from "react";
import {
  ImageCrop,
  ImageCropContent,
} from "@/components/ui/shadcn-io/image-crop";
import type { PercentCrop, PixelCrop } from "react-image-crop";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload } from "lucide-react";

interface ImageUploadStepProps {
  imageFile: File | null;
  onFileSelect: (file: File) => void;
  onClearImage: () => void;
  /** Called with the cropped File whenever crop changes */
  onCropReady: (croppedFile: File) => void;
}

/**
 * Crops the given image file using a PercentCrop and returns a File.
 */
async function cropImageToFile(
  file: File,
  percentCrop: PercentCrop,
): Promise<File> {
  const imageUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(imageUrl);
    const cropX = Math.round((percentCrop.x / 100) * image.naturalWidth);
    const cropY = Math.round((percentCrop.y / 100) * image.naturalHeight);
    const cropWidth = Math.max(
      1,
      Math.round((percentCrop.width / 100) * image.naturalWidth),
    );
    const cropHeight = Math.max(
      1,
      Math.round((percentCrop.height / 100) * image.naturalHeight),
    );

    const canvas = document.createElement("canvas");
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Failed to get canvas context");
    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    );

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
        "image/jpeg",
        0.92,
      );
    });
    return new File([blob], "cropped.jpg", { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function ImageUploadStep({
  imageFile,
  onFileSelect,
  onClearImage,
  onCropReady,
}: ImageUploadStepProps) {
  const latestPercentCrop = useRef<PercentCrop | null>(null);
  const [hasCrop, setHasCrop] = useState(false);

  const handleCropComplete = useCallback(
    (_pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
      if (percentCrop.width <= 0 || percentCrop.height <= 0) return;
      latestPercentCrop.current = percentCrop;
      setHasCrop(true);
    },
    [],
  );

  // Debounce the actual crop generation to avoid excessive canvas work
  useEffect(() => {
    if (!hasCrop || !imageFile || !latestPercentCrop.current) return;

    const percentCrop = latestPercentCrop.current;
    let cancelled = false;

    const timer = setTimeout(() => {
      cropImageToFile(imageFile, percentCrop).then((croppedFile) => {
        if (!cancelled) onCropReady(croppedFile);
      });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hasCrop, imageFile, onCropReady, latestPercentCrop.current]);

  if (!imageFile) {
    return (
      <div className="rounded-xl border-2 border-dashed border-border bg-muted p-12 text-center transition-colors hover:border-border/80">
        <input
          type="file"
          accept="image/*"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              setHasCrop(false);
              latestPercentCrop.current = null;
              onFileSelect(file);
            }
          }}
          className="hidden"
          id="image-upload"
        />
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/20">
          <Upload size={28} className="text-primary" />
        </div>
        <Label
          htmlFor="image-upload"
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
        >
          <Upload size={16} />
          Escolher Imagem
        </Label>
        <p className="mt-4 text-sm text-muted-foreground">
          Suporta JPG, PNG e outros formatos
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <ImageCrop
          aspect={1}
          file={imageFile}
          maxImageSize={5 * 1024 * 1024}
          onComplete={handleCropComplete}
        >
          <ImageCropContent />
        </ImageCrop>
      </div>
      <div className="text-center">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setHasCrop(false);
            latestPercentCrop.current = null;
            onClearImage();
          }}
          className="text-muted-foreground hover:text-foreground"
        >
          Escolher outra imagem
        </Button>
      </div>
    </div>
  );
}
